import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-lab-gauge',
  imports: [CommonModule],
  template: `
    <div class="gauge-row">
      <span class="gauge-label">{{ label() }}</span>

      <div class="gauge-track-wrap">
        <svg class="gauge-svg" viewBox="0 0 1000 100" preserveAspectRatio="none">
          <!-- red left -->
          <rect x="0" y="0" [attr.width]="svgX1()" height="100%" fill="rgba(220,50,50,0.4)"/>
          <!-- yellow left -->
          <rect [attr.x]="svgX1()" y="0"
                [attr.width]="svgX2() - svgX1()"
                height="100%" fill="rgba(255,200,0,0.5)"/>
          <!-- green -->
          <rect [attr.x]="svgX2()" y="0"
                [attr.width]="svgX3() - svgX2()"
                height="100%" fill="rgba(50,180,50,0.5)"/>
          <!-- yellow right -->
          <rect [attr.x]="svgX3()" y="0"
                [attr.width]="svgX4() - svgX3()"
                height="100%" fill="rgba(255,200,0,0.5)"/>
          <!-- red right -->
          <rect [attr.x]="svgX4()" y="0"
                [attr.width]="1000 - svgX4()"
                height="100%" fill="rgba(220,50,50,0.4)"/>

          <!-- ref line -->
          <line [attr.x1]="svgRef()" [attr.x2]="svgRef()" y1="0" y2="100%"
                stroke="black" stroke-width="2" stroke-dasharray="4,3"/>

          @if (predicted() != null) {
            <!-- value indicator: full-height line -->
            <line [attr.x1]="svgPred()" [attr.x2]="svgPred()" y1="0" y2="100%"
                  [attr.stroke]="color()" stroke-width="4" stroke-opacity="0.85"/>
            <!-- value indicator: downward triangle at top -->
            <polygon [attr.points]="triangle()" [attr.fill]="color()"/>
          }
        </svg>

        <div class="gauge-axis">
          <span [style.left.%]="pct(svgX1())">{{ refPlusMin2() | number:'1.1-1' }}</span>
          <span [style.left.%]="pct(svgX2())">{{ refPlusMin1() | number:'1.1-1' }}</span>
          <span [style.left.%]="pct(svgRef())" class="ref-label">{{ ref() | number:'1.1-1' }}</span>
          <span [style.left.%]="pct(svgX3())">{{ refPlusMax1() | number:'1.1-1' }}</span>
          <span [style.left.%]="pct(svgX4())">{{ refPlusMax2() | number:'1.1-1' }}</span>
        </div>
      </div>

      <span class="gauge-value" [style.color]="color()">
        {{ predicted() != null ? (predicted()! | number:'1.2-2') : '—' }}
      </span>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .gauge-row {
      flex: 1; min-height: 0;
      display: flex; align-items: center; gap: 8px;
    }
    .gauge-label { width: 16px; font-weight: bold; font-size: 13px; flex-shrink: 0; }
    .gauge-value { width: 52px; text-align: right; font-size: 12px; font-weight: bold; flex-shrink: 0; }

    .gauge-track-wrap {
      flex: 1; min-width: 0;
      display: flex; flex-direction: column; gap: 2px;
    }
    .gauge-svg {
      width: 100%; height: 32px; display: block;
      border: 1px solid #ccc; border-radius: 3px;
    }

    .gauge-axis { position: relative; height: 14px; font-size: 10px; color: #555; }
    .gauge-axis span { position: absolute; transform: translateX(-50%); white-space: nowrap; }
    .gauge-axis .ref-label { font-weight: bold; }
  `],
})
/**
 * One horizontal threshold gauge for a single LAB channel (L, A, or B).
 *
 * Renders an SVG bar split into 5 coloured zones around a reference value
 * (centre, dashed line), with a coloured indicator marking the predicted
 * value's position:
 *
 *   ┌───────┬─────────┬───────────────┬─────────┬───────┐
 *   │  red  │ yellow  │     green     │ yellow  │  red  │
 *   └───────┴─────────┴───────────────┴─────────┴───────┘
 *           ref+2·min  ref+min   ref+max  ref+2·max
 *                          ▲ ref (dashed)
 *
 * Zone boundaries (the 4 vertical splits, drawn as axis tick labels):
 *   ref + 2·min  →  ref + min  →  ref  →  ref + max  →  ref + 2·max
 *
 * The `min` input is a NEGATIVE number (e.g. −1.5); `max` is positive.
 * Green = predicted is within [ref+min, ref+max].
 * Orange = within [ref+2·min, ref+2·max] but outside green.
 * Red = outside the 2·threshold band.
 *
 * The display range auto-scales each tick so the predicted value is always
 * visible even when it sits well beyond 2·threshold: extent on each side =
 * max(2·|threshold|, 1.5·|predicted − ref|).
 *
 * Used by:
 *   • {@link LabThresholdGaugesComponent} (same dir) — instantiates THREE
 *     of these (one per LAB channel) inside a draggable / resizable
 *     floating panel. That parent feeds each gauge a channel object
 *     {label, predicted, ref, min, max} built from an InspectionModel's
 *     dlMin/Max, daMin/Max, dbMin/Max thresholds plus the predicted Lab
 *     and reference Lab.
 *   • Not used anywhere else — single consumer.
 *
 * Inputs (Angular v17+ signal inputs):
 *   • `label`     REQUIRED string. One-character channel name ("L"/"A"/"B"),
 *                 rendered in the left-hand bold cell.
 *   • `predicted` Optional number. The measured / predicted value to plot.
 *                 When undefined the indicator line + triangle are hidden,
 *                 numeric readout shows "—" and colour is gray.
 *   • `ref`       REQUIRED number. The reference (target) value — centre of
 *                 the gauge, drawn as a dashed vertical line.
 *   • `min`       REQUIRED NEGATIVE number. Lower acceptable threshold
 *                 (delta from ref). Drives ref+min (inner-left) and
 *                 ref+2·min (outer-left) zone boundaries.
 *   • `max`       REQUIRED POSITIVE number. Upper acceptable threshold.
 *                 Drives ref+max + ref+2·max boundaries.
 *
 * Computed signals (derived — do not pass in):
 *   • `refPlusMin2/Min1/Max1/Max2`  axis-tick threshold values.
 *   • `svgX1..X4`, `svgRef`, `svgPred`  the four band-boundary + ref +
 *     predicted positions mapped onto the SVG's 0–1000 viewBox via
 *     {@link toSvg}, which itself uses the auto-scaled
 *     {@link displayLo}/{@link displayHi} window.
 *   • `color`     Indicator + numeric-readout colour: green / orange / red /
 *                 gray (when predicted is undefined). Drives both the SVG
 *                 line/triangle fill and the value cell's text colour.
 *   • `triangle`  3-point string for the downward-pointing apex marker at
 *                 the top of the SVG — drawn together with the full-height
 *                 vertical line at the predicted position.
 *
 * Helpers:
 *   • `pct(svgCoord)`  used in the template to position the absolute
 *     axis-label spans by converting an SVG x-coord (0–1000) to a CSS %.
 */
export class LabGaugeComponent {
  readonly label    = input.required<string>();
  readonly predicted = input<number | undefined>(undefined);
  readonly ref      = input.required<number>();
  /** negative number — lower threshold */
  readonly min      = input.required<number>();
  /** positive number — upper threshold */
  readonly max      = input.required<number>();

  // Derived axis values (threshold positions)
  readonly refPlusMin2 = computed(() => this.ref() + 2 * this.min());
  readonly refPlusMin1 = computed(() => this.ref() + this.min());
  readonly refPlusMax1 = computed(() => this.ref() + this.max());
  readonly refPlusMax2 = computed(() => this.ref() + 2 * this.max());

  // Display range: each side extends to at least 1.5 × |predicted − ref|,
  // so total scale ≥ 3 × distance of actual value from reference.
  private readonly displayLo = computed(() => {
    const d = this.predicted() != null ? Math.abs(this.predicted()! - this.ref()) : 0;
    const extent = Math.max(Math.abs(this.min()) * 2, d * 1.5);
    return this.ref() - extent;
  });

  private readonly displayHi = computed(() => {
    const d = this.predicted() != null ? Math.abs(this.predicted()! - this.ref()) : 0;
    const extent = Math.max(this.max() * 2, d * 1.5);
    return this.ref() + extent;
  });

  // SVG x coordinates (0–1000) using the dynamic display range
  private toSvg(val: number): number {
    const lo = this.displayLo();
    const hi = this.displayHi();
    return Math.max(0, Math.min(1000, ((val - lo) / (hi - lo)) * 1000));
  }

  readonly svgX1  = computed(() => this.toSvg(this.refPlusMin2()));
  readonly svgX2  = computed(() => this.toSvg(this.refPlusMin1()));
  readonly svgRef = computed(() => this.toSvg(this.ref()));
  readonly svgX3  = computed(() => this.toSvg(this.refPlusMax1()));
  readonly svgX4  = computed(() => this.toSvg(this.refPlusMax2()));
  readonly svgPred = computed(() =>
    this.predicted() != null ? this.toSvg(this.predicted()!) : 500
  );

  pct(svgCoord: number): number { return svgCoord / 10; }

  readonly color = computed((): string => {
    const p = this.predicted();
    if (p == null) return 'gray';
    const d = p - this.ref();
    if (d >= this.min() && d <= this.max()) return 'green';
    if (d >= 2 * this.min() && d <= 2 * this.max()) return 'orange';
    return 'red';
  });

  readonly triangle = computed((): string => {
    const cx = this.svgPred();
    // downward-pointing triangle, apex at y=20, base at y=0
    return `${cx},20 ${cx - 16},0 ${cx + 16},0`;
  });
}
